/**
 * main.tsx -- Thin V2 shell with 3-chunk lazy split (Phase 3.5.1 Plan 19 Task 2).
 *
 * Replaces the W7.5 baseline (AdminTree inline + LegacyApp lazy) with a fully
 * symmetric 3-chunk layout. Each surface owns its own provider stack inside its
 * own lazy chunk -- this shell ONLY hosts I18nextProvider + BrowserRouter +
 * an outer ErrorBoundary.
 *
 * Per-surface isolation (D-D1):
 *   surface === 'admin'    -> <AdminTree />     (chunk: src/ui/admin/AdminTree)
 *   surface === 'business' -> <BusinessTree />  (chunk: src/ui/business/BusinessTree)
 *   surface === 'landing'  -> <LegacyApp />     (chunk: legacy_v1/mainV1; T-3.5.1-26 mitigation)
 *
 * Provider scoping (D-D2):
 *   The per-surface auth providers (admin / business) are NOT mounted here.
 *   Each surface scopes its own auth provider inside its lazy chunk. This means
 *   a user on `business.troovee.app` never instantiates the admin auth
 *   provider and vice versa -- the onAuthStateChanged subscription is
 *   per-surface (see ./ui/admin/AdminTree and ./ui/business/BusinessTree).
 *
 * Plan 20 cascade (Option B per checker WARNING #3, revision 2026-05-16):
 *   The `LegacyApp` lazy import points to `../legacy_v1/mainV1` -- a file that
 *   does NOT yet exist on the filesystem at the time Plan 19 commits. Plan 20
 *   Wave 5 runs `git mv src/mainV1.tsx legacy_v1/mainV1.tsx`, which resolves
 *   the path. Between Plan 19 commit and Plan 20 Task 5 commit there is an
 *   intentional TS-broken window: `tsc --noEmit` succeeds thanks to the
 *   @ts-expect-error directive below; `npm run build` will fail at import
 *   resolution until the file is moved. This is documented + gated (Plan 22
 *   atomic cutover is the deploy gate; the broken intermediate state is never
 *   deployed). Plan 20 Task 5 NEW removes this directive after the move.
 *
 * Threat T-3.5.1-26 mitigation:
 *   All three chunks are `lazy(() => import(...))`. Vite emits separate chunks;
 *   the surface-detect routes only one. Admin code never ships to the business
 *   bundle, nor vice versa.
 *
 * @see .planning/phases/03.5.1-business-foundation-v1-isolation-business/03.5.1-19-PLAN.md Task 2
 * @see .planning/phases/03.5.1-business-foundation-v1-isolation-business/03.5.1-CONTEXT.md D-D1/D-D2
 */
import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { I18nextProvider, useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { AlertCircle } from 'lucide-react';

import './index.css';
import { detectSurface } from './ui/surface/surface-detect';
import { ErrorBoundary } from './ui/shared/components/ErrorBoundary';
import { DealSpotLogo } from './ui/shared/components/branding/DealSpotLogo';
import { Button } from './ui/shared/components/forms/Button';
import i18n from './i18n';
import { initSentry } from './shared/errors/sentry.client';

// Phase 3.6 Wave 0 (D-03) — Initialize Sentry React SDK BEFORE the first
// createRoot() so SDK hooks are in place when React mounts. The call is
// fail-soft (skips in dev + when DSN absent) — see sentry.client.ts.
initSentry();

// Three chunks -- true per-surface isolation (D-D1 / T-3.5.1-26 mitigation).
const AdminTree = lazy(() => import('./ui/admin/AdminTree'));
const BusinessTree = lazy(() => import('./ui/business/BusinessTree'));
const LegacyApp = lazy(() => import('../legacy_v1/mainV1'));

/**
 * DL-5 loading layout (UI-SPEC SS Loading state).
 * Shown while the matched surface chunk is being fetched.
 */
function ShellLoadingFallback(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="bg-gray-50 min-h-screen flex items-center justify-center p-4">
      <p className="text-sm text-gray-500">
        {t('common.loading')}
        <span className="loading-dots" />
      </p>
    </div>
  );
}

/**
 * Phase 3.6 Wave 0 (D-03 / SC#15 / UI-SPEC §"Surface 4 — Error UX System") —
 * Sentry React boundary fallback. Renders ONLY when an uncaught React render
 * error escapes every component below. Outer ErrorBoundary (legacy DL-3 from
 * 3.5.1) stays as the second line of defense for non-Sentry errors.
 *
 * Translation reads with i18n.t direct (no useTranslation hook needed — this
 * is a leaf render function passed to Sentry, not a React component lifecycle).
 */
function SentryFallback({ resetError }: { error: unknown; resetError: () => void }) {
  return (
    <div className="bg-gray-50 min-h-screen flex items-center justify-center p-4">
      <div className="bg-white border border-gray-200 rounded-[2rem] shadow-sm p-6 sm:p-8 w-full max-w-md">
        <DealSpotLogo variant="header" />
        <div className="mt-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="w-6 h-6 text-red-600" aria-hidden="true" />
            <h1 className="text-xl font-bold text-gray-900">
              {i18n.t('common.error.boundary.title')}
            </h1>
          </div>
          <p className="text-sm text-gray-600 mb-6">
            {i18n.t('common.error.boundary.body')}
          </p>
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            onClick={() => {
              resetError();
              window.location.reload();
            }}
          >
            {i18n.t('common.error.boundary.reload')}
          </Button>
          <p className="text-xs text-gray-400 text-center mt-4">
            {i18n.t('common.error.boundary.errorId', { id: Sentry.lastEventId() ?? '—' })}
          </p>
        </div>
      </div>
    </div>
  );
}

function App(): React.ReactElement {
  const surface = detectSurface();
  return (
    <Sentry.ErrorBoundary fallback={SentryFallback}>
      <ErrorBoundary>
        <I18nextProvider i18n={i18n}>
          <BrowserRouter>
            <Suspense fallback={<ShellLoadingFallback />}>
              {surface === 'admin' && <AdminTree />}
              {surface === 'business' && <BusinessTree />}
              {surface === 'landing' && <LegacyApp />}
            </Suspense>
          </BrowserRouter>
        </I18nextProvider>
      </ErrorBoundary>
    </Sentry.ErrorBoundary>
  );
}

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found');
}
createRoot(rootEl).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
